import type { BrowserOptions, EdgeOptions, NodeOptions } from '@sentry/nextjs'
import { shouldDropSentryEvent } from '@/lib/sentry/filter'

/** Conservative defaults — keep `/monitoring` tunnel volume under Sentry rate limits. */
export const SENTRY_DEFAULT_TRACES_SAMPLE_RATE = 0.1
export const SENTRY_DEFAULT_REPLAYS_SESSION_SAMPLE_RATE = 0.05
export const SENTRY_DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE = 0.25

function parseSampleRate(value: string | undefined): number | undefined {
  if (!value) return undefined

  const rate = Number.parseFloat(value)
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    return undefined
  }

  return rate
}

function resolveEnvironment(): string {
  return (
    process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT?.trim() ||
    process.env.SENTRY_ENVIRONMENT?.trim() ||
    process.env.NEXT_PUBLIC_VERCEL_ENV?.trim() ||
    process.env.VERCEL_ENV?.trim() ||
    process.env.NODE_ENV ||
    'development'
  )
}

function getCommonOptions(
  dsn: string | undefined,
  tracesSampleRate: string | undefined,
  defaultTracesSampleRate = SENTRY_DEFAULT_TRACES_SAMPLE_RATE
): Pick<
  NodeOptions,
  | 'beforeSend'
  | 'dsn'
  | 'enabled'
  | 'enableLogs'
  | 'environment'
  | 'release'
  | 'sendDefaultPii'
  | 'tracesSampleRate'
> {
  const environment = resolveEnvironment()
  return {
    dsn,
    enabled: Boolean(dsn) && environment !== 'development',
    enableLogs: true,
    environment,
    release: process.env.SENTRY_RELEASE,
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(tracesSampleRate) ?? defaultTracesSampleRate,
    beforeSend(event, hint) {
      if (shouldDropSentryEvent(event, hint)) {
        return null
      }

      if (event.user) {
        delete event.user.email
        delete event.user.ip_address
        delete event.user.username
      }

      if (event.request) {
        delete event.request.cookies
        delete event.request.data

        if (event.request.headers) {
          const headers = { ...event.request.headers }
          delete headers.authorization
          delete headers.Authorization
          delete headers.cookie
          delete headers.Cookie
          event.request.headers = headers
        }
      }

      return event
    },
  }
}

export function getClientSentryOptions(): BrowserOptions {
  return {
    ...getCommonOptions(
      process.env.NEXT_PUBLIC_SENTRY_DSN,
      process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
      SENTRY_DEFAULT_TRACES_SAMPLE_RATE
    ),
    // Browser log drain adds tunnel traffic; errors/replays are enough on the client.
    enableLogs: false,
    replaysSessionSampleRate:
      parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) ??
      SENTRY_DEFAULT_REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate:
      parseSampleRate(process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) ??
      SENTRY_DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE,
    sendDefaultPii: true,
  }
}

/** Session Replay flush tuning — reduces `/monitoring` tunnel POST bursts (Sentry 429). */
export function getReplayIntegrationOptions() {
  return {
    flushMinDelay: 10_000,
    flushMaxDelay: 30_000,
    maskAllInputs: true,
    blockAllMedia: true,
  } as const
}

export function getServerSentryOptions(): NodeOptions {
  return {
    ...getCommonOptions(process.env.SENTRY_DSN, process.env.SENTRY_TRACES_SAMPLE_RATE),
  }
}

export function getEdgeSentryOptions(): EdgeOptions {
  return {
    ...getCommonOptions(process.env.SENTRY_DSN, process.env.SENTRY_TRACES_SAMPLE_RATE),
  }
}
